import { useEffect, useMemo, useRef, useState } from "react";
import type {
  AdminDashboardDependencies,
  AdminDashboardDependencies as AdminDashboardDependenciesType,
} from "../../app/composition/create-admin-dashboard-dependencies";
import { hasAdminPermission, type AdminPermission } from "../../contracts/admin-permissions";
import type { AdminQueryError } from "../../contracts/admin-query-errors";
import {
  ADMIN_FULL_HISTORY_TIME_RANGE_START_AT,
  ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  type AdminRecognitionAuditDetail,
  type AdminRecognitionAuditPage,
  type AdminRecognitionAuditQuery,
  type AdminRecognitionRawAccessGrant,
  type AdminRecognitionRawAccessRequest,
} from "../../contracts/admin-read-models";
import {
  createIdleAdminScreenState,
  createLoadingAdminScreenState,
  type AdminScreenState,
} from "../../contracts/admin-screen-state";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { createAdminPermissionDeniedState } from "../../shared/query/create-admin-permission-denied-state";
import { localizeAdminQueryError } from "../../shared/query/localize-admin-query-error";
import { normalizeAdminQueryError } from "../../shared/query/normalize-admin-query-error";
import { toAdminScreenErrorState } from "../../shared/query/to-admin-screen-error-state";
import { createRecognitionAuditUseCases } from "./recognition-audits-use-cases";
import {
  toRecognitionAuditDetailViewModel,
  toRecognitionAuditListViewModel,
  type RecognitionAuditDetailViewModel,
  type RecognitionAuditListViewModel,
} from "./recognition-audit-view-model";

export type RecognitionAuditRawAccessDialogState = {
  grant?: AdminRecognitionRawAccessGrant;
  grantTarget?: Pick<AdminRecognitionRawAccessRequest, "recognitionRunId" | "targetKind" | "targetId">;
  error?: AdminQueryError;
  submitting: boolean;
};

export type RecognitionAuditScreenModelState = {
  queryDraft: AdminRecognitionAuditQuery;
  activeQuery: AdminRecognitionAuditQuery;
  page?: RecognitionAuditListViewModel;
  pageData?: AdminRecognitionAuditPage;
  selectedRunId?: string;
  detail?: RecognitionAuditDetailViewModel;
  detailData?: AdminRecognitionAuditDetail;
  rawAccessDialog: RecognitionAuditRawAccessDialogState;
  loadState: AdminScreenState<RecognitionAuditListViewModel>;
  error?: AdminQueryError;
};

type RecognitionAuditControllerDependencies = Pick<
  AdminDashboardDependenciesType,
  "adminPermissions" | "adminRoles" | "queryPort" | "runtimeConfig"
>;

export type RecognitionAuditScreenModelDependencies = RecognitionAuditControllerDependencies & {
  locale?: AdminDashboardLocale;
};

export type RecognitionAuditScreenModelController = {
  applyQuery: () => Promise<void>;
  getState: () => RecognitionAuditScreenModelState;
  load: () => Promise<void>;
  selectRun: (recognitionRunId: string) => Promise<void>;
  submitRawAccess: (input: AdminRecognitionRawAccessRequest) => Promise<AdminRecognitionRawAccessGrant | undefined>;
  subscribe: (listener: (state: RecognitionAuditScreenModelState) => void) => () => void;
  updateQueryDraft: (patch: Partial<AdminRecognitionAuditQuery>) => void;
};

export type RecognitionAuditDetailScreenModelState = {
  detail?: RecognitionAuditDetailViewModel;
  detailData?: AdminRecognitionAuditDetail;
  rawAccessDialog: RecognitionAuditRawAccessDialogState;
  loadState: AdminScreenState<RecognitionAuditDetailViewModel>;
  error?: AdminQueryError;
};

export type RecognitionAuditDetailScreenModelController = {
  getState: () => RecognitionAuditDetailScreenModelState;
  load: () => Promise<void>;
  submitRawAccess: (input: AdminRecognitionRawAccessRequest) => Promise<AdminRecognitionRawAccessGrant | undefined>;
  subscribe: (listener: (state: RecognitionAuditDetailScreenModelState) => void) => () => void;
};

export function createDefaultRecognitionAuditQuery(): AdminRecognitionAuditQuery {
  const now = new Date();

  return {
    timeRange: {
      from: ADMIN_FULL_HISTORY_TIME_RANGE_START_AT,
      to: now.toISOString(),
    },
    documentKinds: [],
    statuses: [],
    outcomes: [],
    limit: ADMIN_LIST_QUERY_DEFAULT_LIMIT,
    sort: "startedAt.desc",
  };
}

function createInitialState(
  initialQuery: AdminRecognitionAuditQuery | undefined,
): RecognitionAuditScreenModelState {
  const query = initialQuery ?? createDefaultRecognitionAuditQuery();

  return {
    queryDraft: query,
    activeQuery: query,
    rawAccessDialog: {
      submitting: false,
    },
    loadState: createIdleAdminScreenState(),
  };
}

function toPermissionDeniedState(locale: AdminDashboardLocale): RecognitionAuditScreenModelState {
  return {
    ...createInitialState(undefined),
    loadState: createAdminPermissionDeniedState(
      locale,
      {
        en: "Recognition audit read permission denied.",
        ko: "인식 감사 조회 권한이 없습니다.",
      },
      "admin_recognition_audit_permission_denied",
    ),
  };
}

function createInitialDetailState(): RecognitionAuditDetailScreenModelState {
  return {
    rawAccessDialog: { submitting: false },
    loadState: createIdleAdminScreenState(),
  };
}

function redactRawAccessGrantUrls(grant: AdminRecognitionRawAccessGrant): AdminRecognitionRawAccessGrant {
  const { downloadUrl: _downloadUrl, readUrl: _readUrl, ...safeGrant } = grant;
  return safeGrant;
}

function isMatchingRawAccessGrant(
  input: AdminRecognitionRawAccessRequest,
  grant: AdminRecognitionRawAccessGrant,
): boolean {
  return grant.recognitionRunId === input.recognitionRunId
    && grant.targetKind === input.targetKind
    && grant.targetId === input.targetId;
}

function createRawAccessGrantMismatchError(): AdminQueryError {
  return {
    code: "ADMIN_QUERY_INTERNAL_ERROR",
    message: "Raw access grant did not match the requested target.",
  };
}

function toDetailPermissionDeniedState(locale: AdminDashboardLocale): RecognitionAuditDetailScreenModelState {
  return {
    ...createInitialDetailState(),
    loadState: createAdminPermissionDeniedState(
      locale,
      {
        en: "Recognition audit detail read permission denied.",
        ko: "인식 감사 상세 조회 권한이 없습니다.",
      },
      "admin_recognition_audit_permission_denied",
    ),
  };
}

function createDependenciesKey(
  dependencies: RecognitionAuditScreenModelDependencies,
  initialQuery: AdminRecognitionAuditQuery | undefined,
): string {
  return JSON.stringify({
    roles: dependencies.adminRoles,
    locale: dependencies.locale ?? "ko",
    initialQuery: initialQuery ?? null,
  });
}

export function createRecognitionAuditScreenModelController(
  dependencies: RecognitionAuditScreenModelDependencies,
  initialQuery?: AdminRecognitionAuditQuery,
): RecognitionAuditScreenModelController {
  const locale = dependencies.locale ?? "ko";
  const listeners = new Set<(state: RecognitionAuditScreenModelState) => void>();
  const useCases = createRecognitionAuditUseCases(dependencies.queryPort);
  let listRequestSequence = 0;
  let detailRequestSequence = 0;
  let state = createInitialState(initialQuery);

  const emit = () => {
    for (const listener of listeners) {
      listener(state);
    }
  };

  const setState = (nextState: RecognitionAuditScreenModelState) => {
    state = nextState;
    emit();
  };

  const setPartialState = (patch: Partial<RecognitionAuditScreenModelState>) => {
    setState({
      ...state,
      ...patch,
    });
  };

  const loadList = async (query: AdminRecognitionAuditQuery): Promise<void> => {
    if (!hasAdminPermission(dependencies.adminPermissions, "admin.recognition_audits.read")) {
      setState(toPermissionDeniedState(locale));
      return;
    }

    const requestSequence = ++listRequestSequence;
    setPartialState({
      activeQuery: query,
      error: undefined,
      loadState: createLoadingAdminScreenState(state.page),
    });

    try {
      const envelope = await useCases.loadList(query);
      if (requestSequence !== listRequestSequence) {
        return;
      }

      const viewModel = toRecognitionAuditListViewModel(envelope.data, envelope.meta, {
        activeQuery: query,
        locale,
        permissions: dependencies.adminPermissions,
      });

      setPartialState({
        activeQuery: query,
        page: viewModel,
        pageData: envelope.data,
        loadState:
          envelope.data.items.length === 0
            ? {
                status: "empty",
              }
            : {
                status: "ready",
                viewModel,
              },
      });
    } catch (error) {
      if (requestSequence !== listRequestSequence) {
        return;
      }

      const nextState = toAdminScreenErrorState<RecognitionAuditListViewModel>(locale, error);
      setPartialState({
        error: nextState.error,
        loadState: nextState,
      });
    }
  };

  const loadDetail = async (recognitionRunId: string): Promise<void> => {
    const requestSequence = ++detailRequestSequence;

    try {
      const envelope = await useCases.loadDetail(recognitionRunId);
      if (requestSequence !== detailRequestSequence) {
        return;
      }

      setPartialState({
        selectedRunId: recognitionRunId,
        detail: toRecognitionAuditDetailViewModel(envelope.data, {
          locale,
          permissions: dependencies.adminPermissions,
        }),
        detailData: envelope.data,
      });
    } catch (error) {
      if (requestSequence !== detailRequestSequence) {
        return;
      }

      const nextState = toAdminScreenErrorState<RecognitionAuditListViewModel>(locale, error);
      setPartialState({
        error: nextState.error,
      });
    }
  };

  return {
    async applyQuery() {
      const nextQuery = {
        ...state.queryDraft,
        cursor: undefined,
      };
      setPartialState({
        queryDraft: nextQuery,
      });
      await loadList(nextQuery);
    },
    getState() {
      return state;
    },
    async load() {
      await loadList(state.activeQuery);
    },
    async selectRun(recognitionRunId: string) {
      if (state.selectedRunId === recognitionRunId && state.detail) {
        return;
      }

      await loadDetail(recognitionRunId);
    },
    async submitRawAccess(input: AdminRecognitionRawAccessRequest) {
      if (!hasAdminPermission(dependencies.adminPermissions, "admin.recognition_audits.raw_access")) {
        setPartialState({
          rawAccessDialog: {
            submitting: false,
            error: {
              code: "ADMIN_QUERY_PERMISSION_DENIED",
              message: "Raw access permission denied.",
            },
          },
        });
        return;
      }

      setPartialState({
        rawAccessDialog: {
          ...state.rawAccessDialog,
          submitting: true,
          error: undefined,
          grant: undefined,
          grantTarget: undefined,
        },
      });

      let result;
      try {
        result = await useCases.requestRawAccess(input);
      } catch (error) {
        setPartialState({
          rawAccessDialog: {
            submitting: false,
            error: localizeAdminQueryError(normalizeAdminQueryError(error), locale),
          },
        });
        return;
      }

      if (!result.ok) {
        setPartialState({
          rawAccessDialog: {
            submitting: false,
            error: result.error,
          },
        });
        return;
      }

      if (!isMatchingRawAccessGrant(input, result.envelope.data)) {
        setPartialState({
          rawAccessDialog: { submitting: false, error: createRawAccessGrantMismatchError() },
        });
        return;
      }

      setPartialState({
        rawAccessDialog: {
          submitting: false,
          grant: redactRawAccessGrantUrls(result.envelope.data),
          grantTarget: {
            recognitionRunId: input.recognitionRunId,
            targetKind: input.targetKind,
            targetId: input.targetId,
          },
        },
      });

      await loadDetail(input.recognitionRunId);
      return result.envelope.data;
    },
    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => {
        listeners.delete(listener);
      };
    },
    updateQueryDraft(patch: Partial<AdminRecognitionAuditQuery>) {
      setPartialState({
        queryDraft: {
          ...state.queryDraft,
          ...patch,
        },
      });
    },
  };
}

export function createRecognitionAuditDetailScreenModelController(
  dependencies: RecognitionAuditScreenModelDependencies,
  recognitionRunId: string,
): RecognitionAuditDetailScreenModelController {
  const locale = dependencies.locale ?? "ko";
  const listeners = new Set<(state: RecognitionAuditDetailScreenModelState) => void>();
  const useCases = createRecognitionAuditUseCases(dependencies.queryPort);
  let requestSequence = 0;
  let state = createInitialDetailState();

  const emit = () => {
    for (const listener of listeners) {
      listener(state);
    }
  };
  const setState = (nextState: RecognitionAuditDetailScreenModelState) => {
    state = nextState;
    emit();
  };
  const setPartialState = (patch: Partial<RecognitionAuditDetailScreenModelState>) => {
    setState({ ...state, ...patch });
  };
  const loadDetail = async (): Promise<void> => {
    if (!hasAdminPermission(dependencies.adminPermissions, "admin.recognition_audits.read")) {
      setState(toDetailPermissionDeniedState(locale));
      return;
    }

    const currentRequestSequence = ++requestSequence;
    setPartialState({ error: undefined, loadState: createLoadingAdminScreenState(state.detail) });
    try {
      const envelope = await useCases.loadDetail(recognitionRunId);
      if (currentRequestSequence !== requestSequence) {
        return;
      }
      const detail = toRecognitionAuditDetailViewModel(envelope.data, {
        locale,
        permissions: dependencies.adminPermissions,
      });
      setPartialState({
        detail,
        detailData: envelope.data,
        loadState: { status: "ready", viewModel: detail },
      });
    } catch (error) {
      if (currentRequestSequence !== requestSequence) {
        return;
      }
      const nextState = toAdminScreenErrorState<RecognitionAuditDetailViewModel>(locale, error);
      setPartialState({ error: nextState.error, loadState: nextState });
    }
  };

  return {
    getState: () => state,
    load: loadDetail,
    async submitRawAccess(input) {
      if (!hasAdminPermission(dependencies.adminPermissions, "admin.recognition_audits.raw_access")) {
        setPartialState({
          rawAccessDialog: {
            submitting: false,
            error: { code: "ADMIN_QUERY_PERMISSION_DENIED", message: "Raw access permission denied." },
          },
        });
        return;
      }
      setPartialState({
        rawAccessDialog: { ...state.rawAccessDialog, submitting: true, error: undefined, grant: undefined, grantTarget: undefined },
      });
      try {
        const result = await useCases.requestRawAccess(input);
        if (!result.ok) {
          setPartialState({ rawAccessDialog: { submitting: false, error: result.error } });
          return;
        }
        if (!isMatchingRawAccessGrant(input, result.envelope.data)) {
          setPartialState({
            rawAccessDialog: { submitting: false, error: createRawAccessGrantMismatchError() },
          });
          return;
        }
        setPartialState({
          rawAccessDialog: {
            submitting: false,
            grant: redactRawAccessGrantUrls(result.envelope.data),
            grantTarget: {
              recognitionRunId: input.recognitionRunId,
              targetKind: input.targetKind,
              targetId: input.targetId,
            },
          },
        });
        await loadDetail();
        return result.envelope.data;
      } catch (error) {
        setPartialState({
          rawAccessDialog: {
            submitting: false,
            error: localizeAdminQueryError(normalizeAdminQueryError(error), locale),
          },
        });
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },
  };
}

export function useRecognitionAuditScreenModel(
  dependencies: RecognitionAuditScreenModelDependencies,
  initialQuery?: AdminRecognitionAuditQuery,
): RecognitionAuditScreenModelState & {
  applyQuery: () => Promise<void>;
  load: () => Promise<void>;
  selectRun: (recognitionRunId: string) => Promise<void>;
  submitRawAccess: (input: AdminRecognitionRawAccessRequest) => Promise<AdminRecognitionRawAccessGrant | undefined>;
  updateQueryDraft: (patch: Partial<AdminRecognitionAuditQuery>) => void;
} {
  const key = createDependenciesKey(dependencies, initialQuery);
  const controller = useMemo(
    () => createRecognitionAuditScreenModelController(dependencies, initialQuery),
    [dependencies.queryPort, key],
  );
  const controllerRef = useRef(controller);
  controllerRef.current = controller;

  const [state, setState] = useState<RecognitionAuditScreenModelState>(controller.getState());

  useEffect(() => controller.subscribe(setState), [controller]);
  useEffect(() => {
    void controller.load();
  }, [controller]);

  return {
    ...state,
    applyQuery: controller.applyQuery,
    load: controller.load,
    selectRun: controller.selectRun,
    submitRawAccess: controller.submitRawAccess,
    updateQueryDraft: controller.updateQueryDraft,
  };
}

export function useRecognitionAuditDetailScreenModel(
  dependencies: RecognitionAuditScreenModelDependencies,
  recognitionRunId: string,
): RecognitionAuditDetailScreenModelState & {
  load: () => Promise<void>;
  submitRawAccess: (input: AdminRecognitionRawAccessRequest) => Promise<AdminRecognitionRawAccessGrant | undefined>;
} {
  const key = createDependenciesKey(dependencies, undefined);
  const controller = useMemo(
    () => createRecognitionAuditDetailScreenModelController(dependencies, recognitionRunId),
    [dependencies.queryPort, key, recognitionRunId],
  );
  const [state, setState] = useState<RecognitionAuditDetailScreenModelState>(controller.getState());

  useEffect(() => controller.subscribe(setState), [controller]);
  useEffect(() => {
    void controller.load();
  }, [controller]);

  return { ...state, load: controller.load, submitRawAccess: controller.submitRawAccess };
}
